import { supabase } from '@/shared/lib/supabase';

/**
 * adminReconcileService — staff-side domain reconciliation.
 *
 * Wraps the `domains-reconcile` Edge Function. Discovers domains owned at
 * domains.co.za but not linked in the portal, plus tracks internal-owned
 * domains separately.
 *
 * Spec: _bmad-output/ADMIN_PORTAL_GAP_ANALYSIS_2026-05-13.md §6 A-2.
 */

export interface ReconcileOrphan {
  domain_name: string;
  expiry_date: string | null;
  auto_renew: boolean;
  registrant_email: string | null;
  suggested_customer_id: string | null;
  suggested_customer_email: string | null;
}

export interface ReconcileInternal {
  domain_name: string;
  expiry_date: string | null;
  marked_at: string | null;
  note: string | null;
}

export interface ReconcileSummary {
  reseller_total: number;
  linked_total: number;
  internal_total: number;
  orphan_total: number;
}

export interface ReconcileResponse {
  ok: true;
  snapshot_id: string | null;
  taken_at: string | null;
  is_stale: boolean;
  cached: boolean;
  age_seconds: number;
  summary: ReconcileSummary;
  orphans: ReconcileOrphan[];
  internal: ReconcileInternal[];
}

export const adminReconcileService = {
  /**
   * Fresh discovery — calls the registrar, diffs, classifies. Honours the
   * 60-second server-side cache window unless `force=true` is passed.
   */
  async discover(force = false): Promise<ReconcileResponse> {
    const { data, error } = await supabase.functions.invoke<ReconcileResponse>('domains-reconcile', {
      body: { action: 'discover', force },
    });
    if (error) throw await extractEfError(error);
    if (!data) throw new Error('No reconciliation data returned.');
    return data;
  },

  /**
   * Return the most-recent persisted snapshot without re-pulling from the
   * registrar. Cheap; safe to poll frequently. Used by the orphan-count nav
   * badge.
   */
  async latest(): Promise<ReconcileResponse> {
    const { data, error } = await supabase.functions.invoke<ReconcileResponse>('domains-reconcile', {
      body: { action: 'latest' },
    });
    if (error) throw await extractEfError(error);
    if (!data) throw new Error('No snapshot data returned.');
    return data;
  },

  /** Link an orphan domain to a portal customer. */
  async link(domain_name: string, customer_id: string): Promise<void> {
    const { data, error } = await supabase.functions.invoke<{ ok: true; domain_link_id: string }>(
      'domains-reconcile',
      { body: { action: 'link', domain_name, customer_id } }
    );
    if (error) throw await extractEfError(error);
    if (!data?.ok) throw new Error('Link operation failed.');
  },

  /** Mark a domain as internal-owned (suppress from orphan list). */
  async markInternal(domain_name: string, note?: string): Promise<void> {
    const { error } = await supabase.functions.invoke('domains-reconcile', {
      body: { action: 'mark_internal', domain_name, note },
    });
    if (error) throw await extractEfError(error);
  },

  /** Unmark a domain — it will surface as an orphan again on next discover. */
  async unmarkInternal(domain_name: string): Promise<void> {
    const { error } = await supabase.functions.invoke('domains-reconcile', {
      body: { action: 'unmark_internal', domain_name },
    });
    if (error) throw await extractEfError(error);
  },
};

/**
 * Extract a friendly Error from a Supabase FunctionsHttpError. The structured
 * EF response body lives on error.context (a Response object). Pattern copied
 * from NewServicePage.tsx so admin services surface error codes consistently.
 */
async function extractEfError(error: unknown): Promise<Error> {
  const e = error as Error & { context?: Response };
  const ctx = e.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const parsed = (await ctx.clone().json()) as { code?: string; message?: string };
      if (parsed.message) {
        return new Error(parsed.code ? `${parsed.message} (${parsed.code})` : parsed.message);
      }
    } catch { /* fall through */ }
  }
  return new Error(e.message ?? 'Edge Function call failed.');
}

